import { useEffect, useMemo } from "react"

import { queries, threadDetailQueryArgs } from "@productlane/zero/queries"

import { useZero } from "@/lib/zero"

// Skips rows the pointer only crosses on its way down the list, so a fast
// sweep does not queue a zero-cache hydration per row.
export const THREAD_HOVER_PRELOAD_DELAY_MS = 80

// Released preloads stay synced for this long, which covers a click that comes
// after the pointer drifted off the row.
const THREAD_HOVER_PRELOAD_TTL = "1m"

export interface ThreadHoverPreloader {
	enter: (threadId: string) => void
	leave: () => void
	press: (threadId: string) => void
	dispose: () => void
}

/** Holds at most one preload; starting another releases the previous one. */
export function createThreadHoverPreloader(
	preload: (threadId: string) => () => void,
	delayMs = THREAD_HOVER_PRELOAD_DELAY_MS,
): ThreadHoverPreloader {
	let timer: ReturnType<typeof setTimeout> | undefined
	let active: { threadId: string; release: () => void } | undefined

	const cancel = () => {
		if (timer === undefined) return
		clearTimeout(timer)
		timer = undefined
	}

	const start = (threadId: string) => {
		if (active?.threadId === threadId) return
		active?.release()
		active = { threadId, release: preload(threadId) }
	}

	return {
		enter: (threadId) => {
			cancel()
			timer = setTimeout(() => {
				timer = undefined
				start(threadId)
			}, delayMs)
		},
		leave: cancel,
		press: (threadId) => {
			cancel()
			start(threadId)
		},
		dispose: () => {
			cancel()
			active?.release()
			active = undefined
		},
	}
}

/** Warms a thread's detail query on hover so opening it renders in one pass. */
export function useThreadHoverPreload(): ThreadHoverPreloader {
	const zero = useZero()
	const preloader = useMemo(
		() =>
			createThreadHoverPreloader(
				(threadId) =>
					zero.preload(
						queries.threadWithFullDetails(threadDetailQueryArgs(threadId)),
						{ ttl: THREAD_HOVER_PRELOAD_TTL },
					).cleanup,
			),
		[zero],
	)
	useEffect(() => () => preloader.dispose(), [preloader])
	return preloader
}
